import { useCallback, useEffect, useMemo, useRef } from 'react';
import useStorageOperation from '../useStorageOperation';

interface UsePageStateCacheOptions<T extends Record<string, any>> {
  /** 缓存名前缀，最终 key 为 `${pageName}_${gameId}` */
  pageName: string;
  /** gameId，来自 gameInfo.edition_unified_id */
  gameId: string | undefined;
  /** 获取当前完整状态的函数，在组件卸载时调用，应通过 ref 读取最新值 */
  getState: () => T;
  /** 恢复状态的回调，在组件挂载且有缓存数据时调用 */
  onRestore: (savedState: T) => void;
  /** 滚动容器的 ref，调用 restoreScroll 时用于恢复滚动位置（savedState 中需包含 scrollTop 字段） */
  scrollBodyRef?: React.RefObject<HTMLElement>;
  /** 存储介质，默认 sessionStorage */
  storage?: Storage;
  /** 是否允许恢复缓存状态，默认 true。设为 false 时忽略已缓存的状态（常用于避免非下转返回场景误触发） */
  shouldRestore?: boolean;
}

export interface UsePageStateCacheReturn {
  /** 手动触发滚动恢复，应在子组件数据加载完成后再调用 */
  restoreScroll: () => void;
}

/**
 * 页面状态缓存 Hook
 *
 * 自动处理页面状态的保存和恢复：
 * - 组件挂载时：从 sessionStorage 读取并恢复状态（读取后自动清除）
 * - 组件卸载时：将当前状态保存到 sessionStorage
 *
 * 滚动恢复需要手动调用 restoreScroll()，以便在子组件数据加载完成后再执行。
 *
 * 底层使用 useStorageOperation，配置 model: 'cover' + onlyUseOnce: true
 *
 * @example
 * ```ts
 * const { restoreScroll } = usePageStateCache({
 *   pageName: 'trendingPublications',
 *   gameId: gameInfo?.edition_unified_id,
 *   getState: () => ({
 *     scrollTop: scrollBody?.current?.scrollTop || 0,
 *     value: valueRef.current,
 *   }),
 *   onRestore: (saved) => {
 *     setValue(saved.value || 'total');
 *   },
 *   scrollBodyRef: scrollBody,
 * });
 *
 * // 子组件数据加载完成后调用
 * useEffect(() => {
 *   if (bothChildrenLoaded) {
 *     restoreScroll();
 *   }
 * }, [bothChildrenLoaded]);
 * ```
 */
export default function usePageStateCache<T extends Record<string, any>>({
  pageName,
  gameId,
  getState,
  onRestore,
  scrollBodyRef,
  storage = sessionStorage,
  shouldRestore = true,
}: UsePageStateCacheOptions<T>): UsePageStateCacheReturn {
  const storageKey = useMemo(() => {
    return gameId ? `${pageName}_${gameId}` : '';
  }, [pageName, gameId]);

  const { data: savedState, setStore } = useStorageOperation({
    storage,
    storageKey,
    model: 'cover',
    defaultValue: null,
    onlyUseOnce: true,
  });

  // 缓存 scrollTop，供后续 restoreScroll 使用
  const savedScrollTopRef = useRef(0);
  const shouldRestoreRef = useRef(shouldRestore);
  shouldRestoreRef.current = shouldRestore;

  // 挂载时恢复状态（仅 shouldRestore 为 true 时恢复，滚动需手动调用 restoreScroll）
  useEffect(() => {
    if (savedState && shouldRestore) {
      onRestore(savedState);
      if (savedState.scrollTop) {
        savedScrollTopRef.current = savedState.scrollTop;
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 卸载时保存状态
  useEffect(() => {
    return () => {
      if (!gameId) return;
      setStore(getState());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restoreScroll = useCallback(() => {
    if (!shouldRestoreRef.current) return;
    const scrollTop = savedScrollTopRef.current;
    if (scrollTop > 0 && scrollBodyRef?.current) {
      requestAnimationFrame(() => {
        if (scrollBodyRef?.current) {
          scrollBodyRef.current.scrollTop = scrollTop;
        }
      });
    }
  }, [scrollBodyRef]);

  return { restoreScroll };
}
